// src/plugins/vee-validate-bridge.js
import { h, defineComponent } from 'vue'
import { Form, Field, ErrorMessage, defineRule, configure } from 'vee-validate'
import {
  required, email, min, max, min_value, max_value, numeric, alpha, alpha_num
} from '@vee-validate/rules'

// Regras básicas (adiciona aqui as que usavas no v3)
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('max', max)
defineRule('min_value', min_value)
defineRule('max_value', max_value)
defineRule('numeric', numeric)
defineRule('alpha', alpha)
defineRule('alpha_num', alpha_num)

// Config base (ajusta ao teu comportamento anterior)
configure({
  validateOnInput: true
})

// Wrapper que imita ValidationObserver (v3) usando <Form> (v4)
const ValidationObserverCompat = defineComponent({
  name: 'ValidationObserver',
  props: {
    // v3 tinha props como tag, slim, etc. Aqui ignoramos/encaminhamos mínimas
    tag: { type: String, default: 'div' }
  },
  setup (props, { slots }) {
    return () =>
      h(Form, {}, {
        default: (ctx) => {
          // Mapear props comuns do v3:
          const slotProps = {
            // v3: invalid -> usamos !meta.valid
            invalid: !ctx.meta.valid,
            // v3: passes(cb) -> handleSubmit(cb)
            passes: ctx.handleSubmit,
            // v3: validate() / reset()
            validate: ctx.validate,
            reset: ctx.resetForm,
            errors: ctx.errors,
            meta: ctx.meta
          }
          const content = slots.default ? slots.default(slotProps) : null
          return props.tag ? h(props.tag, {}, content) : content
        }
      })
  }
})

// Wrapper que imita ValidationProvider (v3) usando <Field> (v4)
const ValidationProviderCompat = defineComponent({
  name: 'ValidationProvider',
  props: {
    name: { type: String, default: undefined },
    vid: { type: String, default: undefined }, // id lógico (não usado diretamente)
    rules: { type: [String, Object, Function], default: undefined },
    // no v3 havia 'bails', 'debounce', etc. Ignoramos os raros ou mapeamos depois se precisares
    as: { type: String, default: undefined } // elemento que o v3 criava; v4 usa via slot
  },
  setup (props, { slots, attrs }) {
    return () =>
      h(Field, { name: props.name || props.vid, rules: props.rules }, {
        default: (ctx) => {
          // Aproximar API do v3:
          const classes = {
            // convenções típicas (ajusta se usavas outras)
            'is-valid':  ctx.meta.valid && ctx.meta.validated,
            'is-invalid': !ctx.meta.valid && ctx.meta.validated
          }
          const slotProps = {
            // v3 expunha 'errors', 'classes', 'failedRules', etc.
            errors: ctx.errors,
            errorMessage: ctx.errorMessage,
            classes,
            valid: ctx.meta.valid,
            invalid: !ctx.meta.valid,
            validated: ctx.meta.validated,
            dirty: ctx.meta.dirty,
            meta: ctx.meta,
            // Campos de input prontos
            ...ctx.field
          }
          // Render do conteúdo do utilizador
          const node = slots.default ? slots.default(slotProps) : null

          // Opcionalmente embrulhar num elemento 'as'
          return props.as ? h(props.as, attrs, node) : node
        }
      })
  }
})

export function installVeeValidateBridge (app) {
  // Regista com os nomes antigos:
  app.component('ValidationObserver', ValidationObserverCompat)
  app.component('ValidationProvider', ValidationProviderCompat)
  // Também disponibiliza ErrorMessage (útil em alguns templates)
  app.component('ErrorMessage', ErrorMessage)
}

export { defineRule, configure } // caso queiras configurar noutros ficheiros
